/**
 * Explore engine (POS2-882) — the pure data layer between the shell's URL state
 * (POS2-880) and the views (POS2-883/884). No react-query here: the measure
 * registry, pivot shaping, delta math, and title composition are plain functions
 * so they're unit-testable in isolation and shared verbatim by all four views +
 * the CSV export (POS2-885). The one shape is what makes Table, charts, and CSV
 * provably agree.
 */
import type { Measure, Split } from "@/hooks/use-explore-state";
import type { Locale } from "@/i18n/config";
import type { TFunction } from "@/i18n/catalog";
import type { ExploreWindow, Granularity } from "@/lib/explore-window";
import { currencySymbol, formatCurrency, formatNumber, formatWeight } from "@/lib/format";

/** The 881 pivot response (`GET /waste/events/explore`, `DataResponse` unwrapped).
 * Hand-written per the FE convention — api.types.ts is not regenerated. */
export type ExploreCell = {
  time_key: string;
  split_key: string;
  split_label: string;
  event_count: number;
  total_weight: number;
  total_cost: number;
};
export type ExploreResponse = {
  granularity: Granularity;
  split: Split;
  total_events: number;
  total_weight: number;
  total_cost: number;
  cells: ExploreCell[];
};

// --- Measure registry -------------------------------------------------------

/** The three measures a cell/total carries at once — the measure switch is a
 * client-side accessor swap, never a refetch (all three ship in every payload). */
export type MeasureValues = { cost: number; weight: number; count: number };

/** Everything the explore layer needs beyond the numbers: the currency resolved org→site→kitchen,
 * the reader's locale, and the catalogue. One object rather than three adjacent arguments, because
 * six views call `format` and would otherwise each have to keep them in the right order.
 *
 * `t` lives here rather than being a separate first argument because the same context already
 * travels to `label`, `exploreTitle`, the bucket formatters and the CSV — the wording and the
 * numbers are the same decision (POS2-1134 CP4a/CP4b). */
export type MeasureContext = { currency: string; locale: Locale; t: TFunction };

export type MeasureDef = {
  key: Measure;
  /** Takes the context because the cost measure names the currency — a fixed string here printed
   *  "Waste £" over € figures for every non-GBP org (POS2-1068). */
  label: (ctx: MeasureContext) => string;
  accessor: (v: MeasureValues) => number;
  format: (n: number, ctx: MeasureContext) => string;
};

export const EXPLORE_MEASURES: Record<Measure, MeasureDef> = {
  gbp: {
    key: "gbp",
    // The key is "gbp" for URL back-compat only; the label follows the resolved currency.
    label: ({ currency, locale, t }) =>
      t("waste.measureCost", { symbol: currencySymbol(locale, currency) }),
    accessor: (v) => v.cost,
    format: (n, { currency, locale }) => formatCurrency(locale, n, currency),
  },
  kg: {
    key: "kg",
    label: ({ t }) => t("waste.measureWeight"),
    accessor: (v) => v.weight,
    format: (n, { locale }) => formatWeight(locale, n),
  },
  caps: {
    key: "caps",
    label: ({ t }) => t("waste.measureCaptures"),
    accessor: (v) => v.count,
    format: (n, { locale }) => formatNumber(locale, n),
  },
};

// --- Title composition ------------------------------------------------------

/**
 * Split labels, as literal keys — a module-level constant must not resolve `t()`, which would
 * capture the locale at import (POS2-1134 CP3a).
 *
 * Two maps, not one lowercased at the call site: English writes "split by dish" and German writes
 * "aufgeteilt nach Gericht", because a German noun is capitalised wherever it stands. Casing is a
 * language's rule, not a string transform.
 */
export const SPLIT_LABEL_KEYS: Record<Split, string> = {
  none: "waste.split.none",
  dish: "waste.split.dish",
  kitchen: "waste.split.kitchen",
  service: "waste.split.service",
  reason: "waste.split.reason",
};
const SPLIT_IN_TITLE_KEYS: Record<Split, string> = {
  none: "waste.splitInTitle.none",
  dish: "waste.splitInTitle.dish",
  kitchen: "waste.splitInTitle.kitchen",
  service: "waste.splitInTitle.service",
  reason: "waste.splitInTitle.reason",
};
export const GRAN_UNIT_KEYS: Record<Granularity, string> = {
  daily: "waste.granUnit.day",
  weekly: "waste.granUnit.week",
  monthly: "waste.granUnit.month",
};

/** "{Measure} by {day|week|month}[, split by {split}]" — the panel title. */
export function exploreTitle(
  measure: Measure,
  gran: Granularity,
  split: Split,
  ctx: MeasureContext,
): string {
  const { t } = ctx;
  const base = t("waste.exploreChartTitle", {
    measure: EXPLORE_MEASURES[measure].label(ctx),
    unit: t(GRAN_UNIT_KEYS[gran]),
  });
  return split === "none"
    ? base
    : t("waste.exploreChartTitleSplit", { base, split: t(SPLIT_IN_TITLE_KEYS[split]) });
}

// --- Pivot shaping ----------------------------------------------------------

const ZERO: MeasureValues = { cost: 0, weight: 0, count: 0 };
const SEP = "|";

/** The one shape every view + the CSV consume. Sparse cells are zero-filled on
 * lookup; columns mirror the server's cost ranking (`remainder` always last),
 * NOT the active measure (the top-N cap is by cost — 881 convention 4). */
export type ExplorePivot = {
  rowKeys: string[]; // time_keys, chronological (lexical == chronological)
  colKeys: string[]; // split_keys, window-cost desc, `remainder` last
  labels: Record<string, string>; // split_key → split_label
  cell: (row: string, col: string) => MeasureValues; // zero-filled
  rowTotals: Record<string, MeasureValues>;
  colTotals: Record<string, MeasureValues>;
  grandTotal: MeasureValues; // authoritative window total (response.total_*)
  recordCount: number; // response.total_events
};

function accumulate(map: Map<string, MeasureValues>, key: string, v: MeasureValues) {
  const prev = map.get(key) ?? ZERO;
  map.set(key, {
    cost: prev.cost + v.cost,
    weight: prev.weight + v.weight,
    count: prev.count + v.count,
  });
}

export function shapeExplorePivot(res: ExploreResponse): ExplorePivot {
  const cells = new Map<string, MeasureValues>();
  const rowSet = new Set<string>();
  const rowTotals = new Map<string, MeasureValues>();
  const colTotals = new Map<string, MeasureValues>();
  const labels: Record<string, string> = {};

  for (const c of res.cells) {
    const v: MeasureValues = { cost: c.total_cost, weight: c.total_weight, count: c.event_count };
    cells.set(`${c.time_key}${SEP}${c.split_key}`, v);
    rowSet.add(c.time_key);
    labels[c.split_key] = c.split_label;
    accumulate(rowTotals, c.time_key, v);
    accumulate(colTotals, c.split_key, v);
  }

  const rowKeys = [...rowSet].sort();
  const colKeys = [...colTotals.keys()].sort((a, b) => {
    // `remainder` (the top-N tail) always sorts last; otherwise by window cost desc.
    if (a === "remainder") return 1;
    if (b === "remainder") return -1;
    const d = colTotals.get(b)!.cost - colTotals.get(a)!.cost;
    return d !== 0 ? d : a.localeCompare(b);
  });

  return {
    rowKeys,
    colKeys,
    labels,
    cell: (row, col) => cells.get(`${row}${SEP}${col}`) ?? ZERO,
    rowTotals: Object.fromEntries(rowTotals),
    colTotals: Object.fromEntries(colTotals),
    grandTotal: { cost: res.total_cost, weight: res.total_weight, count: res.total_events },
    recordCount: res.total_events,
  };
}

// --- Delta ------------------------------------------------------------------

/** Signed change vs the prior window: (current − prior) / prior. Returns null
 * when there's no baseline (no prior window, or prior == 0) or the change is
 * below the display floor (|Δ| < 0.5%) — the caller hides the pill on null. */
export function computeDelta(current: number, prior: number | null | undefined): number | null {
  if (prior == null || prior === 0) return null;
  const d = (current - prior) / prior;
  return Math.abs(d) < 0.005 ? null : d;
}

/** The window the delta compares against: the immediately preceding equal-length
 * block `[from − length, from)` — for the granularity default range and any custom
 * range alike (POS2-1000). */
export function precedingWindow(window: ExploreWindow): ExploreWindow {
  const length = window.to.getTime() - window.from.getTime();
  return { from: new Date(window.from.getTime() - length), to: window.from };
}
